import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import {
    BUSINESS_ACCOUNT_SEARCH_TYPE_ENUM,
    BUSINESS_ACCOUNT_STATUS_ENUM,
    COST_TYPE_ENUM,
    IIdAndNameDto,
    ILotDto,
    IProductCentreDocumentDto,
    PRODUCT_SEARCH_TYPE_ENUM,
    UNIT_OF_MEASURE_ENUM
} from '@classictechsolutions/hubapi-transpiled-enums';
import { take } from 'rxjs';
import { IPurchaseOrderLineMappedItem } from '@app/logic/purchase-orders/interfaces/i.purchase-order-line.mapped';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseFormViewDirective } from '@app/shared/base-views/base-form-view.directive';
import { IPurchaseOrderLineDto } from '@app/logic/purchase-orders/interfaces/i.purchase-order-line.dto';
import { IPurchaseOrdersLogicService } from '@app/logic/purchase-orders/interfaces/i.purchase-orders.logic.service';
import { ToastService } from '@app/core/services/toast/toast.service';
import { FormMode } from '@app/shared/enums/form';
import { isNullOrWhiteSpace, toPromise } from 'cb-hub-lib';
import { ProductLogicService } from '@app/logic/products';
import { IProductDto } from '@app/logic/products/interfaces/i.product.dto';
import { IBusinessAccountSearchParams } from '@app/logic/business-accounts/interfaces/i.business-account-search-params';
import { IExistingSlotItem } from '@app/views/products/tabs/product-centre/product-centre-tab.component';
import { NgForm } from '@angular/forms';

interface IData {
    lineItem: IPurchaseOrderLineMappedItem;
    lot: ILotDto;
}

@Component({
    selector: 'cb-edit-manual-order-line-dialog',
    templateUrl: './edit-manual-order-line-dialog.component.html',
    styleUrls: ['./edit-manual-order-line-dialog.component.scss'],
    providers: [
        ToastService,
        ProductLogicService,
    ],
    standalone: false
})
export class EditManualOrderLineDialogComponent extends BaseFormViewDirective<IPurchaseOrderLineDto, IPurchaseOrderLineMappedItem, IPurchaseOrdersLogicService> implements OnInit {
    public static readonly MIN_WIDTH = '66%';

    public get mappedItem(): IPurchaseOrderLineMappedItem {
        return this.data.lineItem;
    }
    public get lot(): ILotDto {
        return this.data.lot;
    }

    public PRODUCT_SEARCH_TYPE_ENUM = PRODUCT_SEARCH_TYPE_ENUM;
    public selectedProduct: IProductCentreDocumentDto;
    public productSearchText: string;
    public uomList = UNIT_OF_MEASURE_ENUM.toSelectList();
    public businessAccountFindParams = {} as Partial<IBusinessAccountSearchParams>;
    public businessEntityIdAndNationalId: Set<number> = new Set();
    public categoryPathString: string;
    public existingSlotItem: IExistingSlotItem;
    public loadingRate = false;
    public mode: FormMode;
    @ViewChild('editLineItemForm') public editLineItemForm: NgForm;

    constructor(
        public readonly dialogRef: MatDialogRef<EditManualOrderLineDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public readonly data: IData,
        public readonly toastService: ToastService,
        public readonly productLogic: ProductLogicService,
    ) {
        super(toastService);
        this.businessEntityIdAndNationalId.add(this.lot.businessEntityId);
        this.businessEntityIdAndNationalId.add(0);
    }

    public ngOnInit(): void {
        this.setupDialog();
        this.businessAccountFindParams = {
            reg: this.lot.location.region.id,
            s: [
                BUSINESS_ACCOUNT_STATUS_ENUM.Active,
                BUSINESS_ACCOUNT_STATUS_ENUM.Overridden
            ],
            t: BUSINESS_ACCOUNT_SEARCH_TYPE_ENUM.All
        };
    }

    public productUpdate(item: IProductCentreDocumentDto, productUpdatedFromDialog = false ): void {
        if(productUpdatedFromDialog){
            this.selectedProduct = item;
            this.editLineItemForm.form.controls.product.markAsDirty();
        }

        this.categoryPathString = this.getCategoryPathFromArray(item.categoryPath);
        this.loadingRate = true;
        this.productLogic
            .getRateForManualPurchaseOrder(item.id, this.mappedItem.parent.id)
            .pipe(take(1)).subscribe({
                next: (rate) => {
                    this.mappedItem.setProductDetails(item, this.productSearchText, rate);
                    this.loadingRate = false;
                }
            });
    }

    public updateLineItemDescription(value: string): void {
        this.productSearchText = value;
        if(value === '' || value === null) {
            this.selectedProduct = null;
        } else if(this.isProductSelected()) {
            // If there is a still a product selected when the search text changed, its possible the search text change
            // was triggered by a key press such as ctrl+c or shift etc.
            return;
        }
        this.mappedItem.description = this.productSearchText;
        this.mappedItem.offeringId = null;
        this.categoryPathString = '';
    }

    public saveDialog(): void {
        toPromise(this.mappedItem.$save()).then(() => {
            this.dialogRef.close(this.mappedItem);
        });
    }

    public uomChanged(): void {
        this.mappedItem.uom = UNIT_OF_MEASURE_ENUM[this.mappedItem.uomId];
    }

    public cancelDialog(): void {
        this.dialogRef.close(false);
    }

    public unknownRateChanged(): void {
        if (this.mappedItem.costToBeDetermined) {
            this.mappedItem.rate = null;
        }
    }

    private getCategoryPathFromArray(categoryPath: any[]): string {
        return categoryPath
            .map(category => (typeof category === 'string' ? category : category.label))
            .join(' > ');
    }

    private readonly setupDialog = (): void => {
        this.mode = this.mappedItem.id > 0 ? FormMode.Edit : FormMode.Add;

        if (this.mappedItem.offeringId) {
            toPromise(this.productLogic.$getItem(this.mappedItem.offeringId)).then((offering: IProductDto) => {
                this.selectedProduct = this.mapProductDtoToProductCentreDocumentDto(offering);
                this.setExistingSlotItem(offering);
                this.categoryPathString = this.getCategoryPathFromArray(offering.categoryPath);
            });
        } else if (!isNullOrWhiteSpace(this.mappedItem.description)) {
            this.selectedProduct = {
                id: null,
                name: this.mappedItem.description,
            } as IProductCentreDocumentDto;
            this.setExistingSlotItem({ id: 0, name: this.mappedItem.description });
        }

        if (!this.mappedItem.isNoteLine) {
            this.productSearchText = this.mappedItem.description;
        }
    };
    protected readonly COST_TYPE_ENUM = COST_TYPE_ENUM;

    private setExistingSlotItem(idAndNameDto: IIdAndNameDto): void {
        this.existingSlotItem = {
            id: idAndNameDto.id ?? 0,
            itemName: idAndNameDto.name
        } as IExistingSlotItem;
    }

    private mapProductDtoToProductCentreDocumentDto(productDto: IProductDto): IProductCentreDocumentDto {
        return {
            categoryPath: productDto.categoryPath,
            categoryTree: [],
            code: productDto.code,
            componentItems: '',
            compositeItemId: productDto.compositeItemId,
            dynamicFilters: {},
            hasImage: productDto.hasImage,
            id: productDto.id,
            imageUrl: '',
            inSpec: productDto.inSpec,
            isActive: productDto.isActive,
            isCompositeItem: productDto.isCompositeItem,
            isStandardProduct: productDto.isStandardProduct,
            mainCategory: productDto.mainCategory,
            manualColourEntryRequired: productDto.manualColourEntryRequired,
            name: productDto.name,
            nationalRate: productDto.nationalRate,
            productType: productDto.productType,
            regions: [],
            showInColourYourDreams: productDto.showInColourYourDreams,
            skus: '',
            tierSortOrder: 0,
            tiers: [],
            uomDisplay: productDto.uomDisplay
        };
    }

    private isProductSelected(): boolean {
        return (this.selectedProduct as IProductCentreDocumentDto)?.id > 0;
    }
}
